import { type ForwardRefExoticComponent, forwardRef } from 'react';

import { InputChipAvatar } from './input-chip-avatar';
import { InputChipClose } from './input-chip-close';
import { InputChipIcon } from './input-chip-icon';
import { InputChipLabel } from './input-chip-label';
import { InputChipRoot } from './input-chip-root';
import type { InputChipPropsInterface } from './types/input-chip.types';

/**
 * Input chips are used to hold text that has been entered or selected
 * by the user (ex: multiple Combobox selections, addresses in an email's "to:" field).
 * When text is converted into an input chip, it helps confirm a user's input.
 *
 * @related ['AssistChip', 'Combobox', 'FilterChip', 'Tag']
 * @since 8.97.0
 */

export const InputChip = forwardRef<HTMLDivElement, InputChipPropsInterface>(
  (props: InputChipPropsInterface, forwardedRef) => {
    const {
      avatar,
      elevated,
      children,
      closeButtonVoiceOver = '',
      disabled,
      icon,
      onClose,
      truncate,
      ...rest
    } = props;

    return (
      <InputChip.Root elevated={elevated} disabled={disabled} ref={forwardedRef} {...rest}>
        {avatar ? <InputChip.Avatar>{avatar}</InputChip.Avatar> : null}
        {icon ? <InputChip.Icon>{icon}</InputChip.Icon> : null}
        <InputChip.Label truncate={truncate}>{String(children)}</InputChip.Label>
        <InputChip.Close title={closeButtonVoiceOver} onClick={onClose} />
      </InputChip.Root>
    );
  },
) as ForwardRefExoticComponent<InputChipPropsInterface> & {
  Root: typeof InputChipRoot;
  Avatar: typeof InputChipAvatar;
  Close: typeof InputChipClose;
  Icon: typeof InputChipIcon;
  Label: typeof InputChipLabel;
};

InputChip.displayName = 'InputChip';

InputChip.Root = InputChipRoot;
InputChip.Avatar = InputChipAvatar;
InputChip.Close = InputChipClose;
InputChip.Icon = InputChipIcon;
InputChip.Label = InputChipLabel;
